import {
  GetAdminSubGroupDataRequest,
  UpdateSubGroupConfigurationRequest,
  FindUserMembershipRequest,
  UserExpirationRequest,
  GetAdminSubGroupsStatsResponse,
  AdminSubGroupStats,
  ApiGeneralDataResponse,
  AssignLicensesRequest,
  LicenseAvailablePackages,
  AssignLicensesResponse,
  ApiResponse,
  CreateInvitationRequest,
  InvitationRequest,
  SubGroupUserMembership,
  Invitation,
  AdminSubGroup,
  SubGroupConfiguration,
  UpdateInvitationRequest,
} from "@shared/types";
import { ApiTags } from "@shared/types";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import { EndpointBuilder } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";

const updateMembershipStatus = (
  args: FindUserMembershipRequest,
  updatedMember: SubGroupUserMembership | undefined
) =>
  sharedLib.sharedApi.admin.util.updateQueryData(
    "getAdminSubGroupData",
    { sub_group_id: args.sub_group_id },
    (subGroup) => {
      if (!updatedMember) return;
      if (subGroup) {
        const groupMembershipIndex = subGroup.members.findIndex(
          (member) => member.id === args.membership_id
        );
        if (groupMembershipIndex !== -1) {
          subGroup.members[groupMembershipIndex] = updatedMember;
        }
      }
    }
  );

const updateInvitation = (
  args: InvitationRequest,
  updatedInvitation: Invitation | undefined
) =>
  sharedLib.sharedApi.admin.util.updateQueryData(
    "getAdminSubGroupData",
    { sub_group_id: args.sub_group_id },
    (subGroup) => {
      if (subGroup?.invitations && updatedInvitation) {
        const invitationIndex = subGroup.invitations.findIndex(
          (invitation) => invitation.id === args.invitation_id
        );
        subGroup.invitations[invitationIndex] = updatedInvitation;
      }
    }
  );

export const adminEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    updateSubGroupConfiguration: build.mutation<
      SubGroupConfiguration | undefined,
      UpdateSubGroupConfigurationRequest
    >({
      query: (updateConfigurationRequest) => ({
        url: "/update_sub_group_configuration/",
        method: "POST",
        body: updateConfigurationRequest,
      }),
      transformResponse: (response: ApiResponse<SubGroupConfiguration>) =>
        response.data,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: configuration } = await queryFulfilled;
          dispatch(
            sharedLib.sharedApi.admin.util.updateQueryData(
              "getAdminSubGroupData",
              { sub_group_id: args.sub_group_id },
              (subGroup) => {
                if (subGroup) {
                  subGroup.configuration = configuration!;
                }
              }
            )
          );
        } catch {}
      },
    }),
    createInvitation: build.mutation<
      Invitation | undefined,
      CreateInvitationRequest
    >({
      query: (createInvitationRequest) => ({
        url: "/create_invitation/",
        method: "POST",
        body: createInvitationRequest,
      }),
      transformResponse: (response: ApiResponse<Invitation>) => response.data,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: createdInvitation } = await queryFulfilled;
          dispatch(
            sharedLib.sharedApi.admin.util.updateQueryData(
              "getAdminSubGroupData",
              { sub_group_id: args.sub_group_id },
              (subGroup) => {
                if (subGroup?.invitations && createdInvitation) {
                  subGroup.invitations.push(createdInvitation);
                }
              }
            )
          );
        } catch {}
      },
    }),
    updateInvitation: build.mutation<
      Invitation | undefined,
      UpdateInvitationRequest
    >({
      query: (updateInvitationRequest) => ({
        url: "/update_invitation/",
        method: "POST",
        body: updateInvitationRequest,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data: updatedInvitation } = await queryFulfilled;
          dispatch(updateInvitation(args, updatedInvitation));
        } catch {}
      },
      transformResponse: (response: ApiResponse<Invitation>) => response.data,
    }),
    deleteInvitation: build.mutation<Invitation | undefined, InvitationRequest>(
      {
        query: (deleteInvitationRequest) => ({
          url: "/delete_invitation/",
          method: "POST",
          body: deleteInvitationRequest,
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: updatedInvitation } = await queryFulfilled;
            dispatch(
              sharedLib.sharedApi.admin.util.updateQueryData(
                "getAdminSubGroupData",
                { sub_group_id: args.sub_group_id },
                (subGroup) => {
                  if (subGroup?.invitations && updatedInvitation) {
                    const invitationIndex = subGroup.invitations.findIndex(
                      (invitation) => invitation.id === args.invitation_id
                    );
                    subGroup.invitations.splice(invitationIndex, 1);
                  }
                }
              )
            );
          } catch {}
        },
        transformResponse: (response: ApiResponse<Invitation>) => response.data,
      }
    ),
    activateInvitation: build.mutation<
      Invitation | undefined,
      InvitationRequest
    >({
      query: (activateInvitation) => ({
        url: "/activate_invitation/",
        method: "POST",
        body: activateInvitation,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data: updatedInvitation } = await queryFulfilled;
          dispatch(updateInvitation(args, updatedInvitation));
        } catch {}
      },
      transformResponse: (response: ApiResponse<Invitation>) => response.data,
    }),
    deactivateInvitation: build.mutation<
      Invitation | undefined,
      InvitationRequest
    >({
      query: (deactivateInvitation) => ({
        url: "/deactivate_invitation/",
        method: "POST",
        body: deactivateInvitation,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data: updatedInvitation } = await queryFulfilled;
          dispatch(updateInvitation(args, updatedInvitation));
        } catch {}
      },
      transformResponse: (response: ApiResponse<Invitation>) => response.data,
    }),
    authorizeUser: build.mutation<
      SubGroupUserMembership | undefined,
      FindUserMembershipRequest
    >({
      query: (authorizeUserRequest) => ({
        url: "/authorize_user/",
        method: "POST",
        body: authorizeUserRequest,
      }),
      transformResponse: (response: ApiResponse<SubGroupUserMembership>) =>
        response.data,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: membership } = await queryFulfilled;
          dispatch(updateMembershipStatus(args, membership));
        } catch {}
      },
    }),
    kickUser: build.mutation<
      SubGroupUserMembership | undefined,
      FindUserMembershipRequest
    >({
      query: (findUserMembershipRequest) => ({
        url: "/kick_user/",
        method: "POST",
        body: findUserMembershipRequest,
      }),
      transformResponse: (response: ApiResponse<SubGroupUserMembership>) =>
        response.data,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: membership } = await queryFulfilled;
          dispatch(updateMembershipStatus(args, membership));
        } catch {}
      },
    }),
    saveUserExpiration: build.mutation<
      SubGroupUserMembership | undefined,
      UserExpirationRequest
    >({
      query: (saveUserExpirationRequest) => ({
        url: "/save_user_expiration/",
        method: "POST",
        body: saveUserExpirationRequest,
      }),
      transformResponse: (response: ApiResponse<SubGroupUserMembership>) =>
        response.data,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: membership } = await queryFulfilled;
          dispatch(updateMembershipStatus(args, membership));
        } catch {}
      },
    }),
    getAdminSubGroups: build.query<AdminSubGroupStats[], void>({
      query: (fetchSubGroupsRequest) => ({
        url: "/get_admin_sub_groups/",
        method: "POST",
        body: fetchSubGroupsRequest,
      }),
      transformResponse: (
        response: ApiResponse<GetAdminSubGroupsStatsResponse>
      ) => response?.data?.sub_groups || [],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            groupConfigurationsFilters.actions.loadGroups({ data: data || [] })
          );
        } catch {}
      },
    }),
    getAdminSubGroupData: build.query<
      AdminSubGroup,
      GetAdminSubGroupDataRequest
    >({
      query: (fetchSubGroupsRequest) => ({
        url: "/get_admin_sub_group_data/",
        method: "POST",
        body: fetchSubGroupsRequest,
      }),
      providesTags: (_, __, args) => [
        { type: ApiTags.ADMIN_SUB_GROUP_DATA, id: args.sub_group_id },
      ],
      transformResponse: (response: ApiResponse<AdminSubGroup>) =>
        response.data,
    }),
    getLicenseAvailablePackages: build.query<
      LicenseAvailablePackages,
      GetAdminSubGroupDataRequest
    >({
      query: (args) => ({
        url: "/get_license_available_packages/",
        method: "POST",
        body: args,
      }),
      providesTags: (_, __, args) => [
        { type: ApiTags.ADMIN_SUB_GROUP_DATA, id: args.sub_group_id },
      ],
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<LicenseAvailablePackages>>
      ) => response.data.data,
    }),
    assignLicenses: build.mutation<
      AssignLicensesResponse,
      AssignLicensesRequest
    >({
      query: (args) => ({
        url: "/assign_licenses/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<AssignLicensesResponse>>
      ) => response.data.data,
      invalidatesTags: (response, _, args) =>
        response
          ? [{ type: ApiTags.ADMIN_SUB_GROUP_DATA, id: args.sub_group_id }]
          : [],
    }),
  }),
  overrideExisting: false,
};
